import { freeze } from 'immer';
import { nanoid } from 'nanoid';
import * as z from 'zod';
import { CharacterId, CharacterIdSchema } from '../../character/characterTypes.ts';
import type { Logger } from '../../logging/logger.ts';
import type { SpaceId } from '../../space/index.ts';
import { Assert, AssertNever, AssertNotNullable, MemoizeNoArg } from '../../utility/misc.ts';
import { EvalContainerPath } from '../appearanceHelpers.ts';
import { ActionTargetSelector, type ItemContainerPath, type ItemPath } from '../appearanceTypes.ts';
import { AppearanceValidationResult } from '../appearanceValidation.ts';
import { AssetManager } from '../assetManager.ts';
import type { Item } from '../item/base.ts';
import type { AppearanceItems } from '../item/index.ts';
import { IExportOptions } from '../modules/common.ts';
import { AssetFrameworkCharacterState } from './characterState.ts';
import { AppearanceBundleSchema, AppearanceClientBundle, AppearanceClientDeltaBundleSchema, type AppearanceBundle, type AppearanceClientDeltaBundle } from './characterStateTypes.ts';
import { GlobalStateAutoProcessCharacterPositions } from './roomGeometry.ts';
import { AssetFrameworkSpaceState, SpaceStateBundleSchema, SpaceStateClientDeltaBundleSchema, type SpaceStateClientBundle, type SpaceStateClientDeltaBundle } from './spaceState.ts';

export const AssetFrameworkGlobalStateBundleSchema = z.object({
	stateId: z.string(),
	characters: z.record(CharacterIdSchema, AppearanceBundleSchema),
	space: SpaceStateBundleSchema,
	clientOnly: z.boolean().optional(),
});
export type AssetFrameworkGlobalStateBundle = z.infer<typeof AssetFrameworkGlobalStateBundleSchema>;
export type AssetFrameworkGlobalStateClientBundle = AssetFrameworkGlobalStateBundle & {
	characters: Record<CharacterId, AppearanceClientBundle>;
	space: SpaceStateClientBundle | null;
	clientOnly: true;
};

type AssetFrameworkGlobalStateCharacterClientDelta =
	| ['full', AppearanceBundle]
	| ['delta', AppearanceClientDeltaBundle]
	| null;
const AssetFrameworkGlobalStateCharacterClientDeltaSchema: z.ZodType<AssetFrameworkGlobalStateCharacterClientDelta> = z.union([
	z.tuple([z.literal('full'), AppearanceBundleSchema]),
	z.tuple([z.literal('delta'), AppearanceClientDeltaBundleSchema]),
	z.null(),
]);

export type AssetFrameworkGlobalStateClientDeltaBundle = {
	originalStateId: string;
	targetStateId: string;
	characters?: Record<CharacterId, AssetFrameworkGlobalStateCharacterClientDelta>;
	space?: SpaceStateClientDeltaBundle;
};
export const AssetFrameworkGlobalStateClientDeltaBundleSchema: z.ZodType<AssetFrameworkGlobalStateClientDeltaBundle> = z.object({
	originalStateId: z.string(),
	targetStateId: z.string(),
	characters: z.record(CharacterIdSchema, AssetFrameworkGlobalStateCharacterClientDeltaSchema).optional(),
	space: SpaceStateClientDeltaBundleSchema.optional(),
});

/**
 * Class that stores immutable state for whole current context (so the current space, be it private or public one).
 *
 * The class is immutable.
 */
export class AssetFrameworkGlobalState {
	public readonly assetManager: AssetManager;
	public readonly characters: ReadonlyMap<CharacterId, AssetFrameworkCharacterState>;
	public readonly space: AssetFrameworkSpaceState;

	/**
	 * Randomly generated ID of this state, used for detecting desyncs.
	 * Internally it is generated lazily to avoid creating it for every intermediate state during an action.
	 */
	private _stateId?: string;

	private constructor(
		assetManager: AssetManager,
		characters: ReadonlyMap<CharacterId, AssetFrameworkCharacterState>,
		space: AssetFrameworkSpaceState,
		stateId?: string,
	) {
		this.assetManager = assetManager;
		this.characters = characters;
		this.space = space;
		this._stateId = stateId;
	}

	public getCharacterState(character: CharacterId): AssetFrameworkCharacterState | null {
		return this.characters.get(character) ?? null;
	}

	/**
	 * Returns a random ID for this state, used for detecting desyncs.
	 */
	public getStateId(): string {
		if (this._stateId == null) {
			this._stateId = nanoid(8);
		}
		return this._stateId;
	}

	public isValid(): boolean {
		return this.validate().success;
	}

	@MemoizeNoArg
	public validate(): AppearanceValidationResult {
		if (this.space != null) {
			const r = this.space.validate();
			if (!r.success)
				return r;
		}

		for (const character of this.characters.values()) {
			const r = character.validate(this.space);
			if (!r.success)
				return r;
		}

		return {
			success: true,
		};
	}

	/**
	 * Get items in specified target and its container.
	 * @param target - The target to look for
	 * @param containerPath - The container to look for
	 * @returns List of items if found, or `null` otherwise
	 */
	public getItems(target: ActionTargetSelector, containerPath?: ItemContainerPath): AppearanceItems | null {
		let result: AppearanceItems;
		if (target.type === 'character') {
			const character = this.getCharacterState(target.characterId);
			if (!character)
				return null;

			result = character.items;
		} else if (target.type === 'room') {
			const room = this.space.getRoom(target.roomId);
			if (!room)
				return null;

			result = room.items;
		} else {
			AssertNever(target);
		}
		if (containerPath != null) {
			return EvalContainerPath(result, containerPath) ?? null;
		}
		return result;
	}

	/**
	 * Get specific item.
	 * @param target - The target to look for the item on
	 * @param itemPath - Path to the item
	 * @returns The item, or `null` if not found
	 */
	public getItem(target: ActionTargetSelector, itemPath: ItemPath): Item | null {
		return this.getItems(target, itemPath.container)
			?.find((it) => it.id === itemPath.itemId) ?? null;
	}

	public exportToBundle(): AssetFrameworkGlobalStateBundle {
		const result: AssetFrameworkGlobalStateBundle = {
			stateId: this.getStateId(),
			characters: {},
			space: this.space.exportToBundle(),
		};

		for (const [characterId, characterState] of this.characters) {
			result.characters[characterId] = characterState.exportToBundle();
		}
		return result;
	}

	public exportToClientBundle(options: IExportOptions = {}): AssetFrameworkGlobalStateClientBundle {
		options.clientOnly = true;
		const result: AssetFrameworkGlobalStateClientBundle = {
			stateId: this.getStateId(),
			characters: {},
			space: this.space.exportToClientBundle(options),
			clientOnly: true,
		};
		for (const [characterId, characterState] of this.characters) {
			result.characters[characterId] = characterState.exportToClientBundle(options);
		}
		return result;
	}

	public exportToClientDeltaBundle(originalState: AssetFrameworkGlobalState, options: IExportOptions = {}): AssetFrameworkGlobalStateClientDeltaBundle {
		Assert(this.assetManager === originalState.assetManager);
		options.clientOnly = true;

		const result: AssetFrameworkGlobalStateClientDeltaBundle = {
			originalStateId: originalState.getStateId(),
			targetStateId: this.getStateId(),
		};

		// Find removed characters
		for (const id of originalState.characters.keys()) {
			if (!this.characters.has(id)) {
				result.characters ??= {};
				result.characters[id] = null;
			}
		}

		// Find added or updated characters
		for (const [id, character] of this.characters) {
			const originalCharacter = originalState.characters.get(id);
			if (originalCharacter !== character) {
				result.characters ??= {};
				if (originalCharacter != null) {
					result.characters[id] = ['delta', character.exportToClientDeltaBundle(originalCharacter, options)];
				} else {
					result.characters[id] = ['full', character.exportToClientBundle(options)];
				}
			}
		}

		// Check if room changed
		if (originalState.space !== this.space) {
			result.space = this.space.exportToClientDeltaBundle(originalState.space, options);
		}

		return result;
	}

	public applyClientDeltaBundle(bundle: AssetFrameworkGlobalStateClientDeltaBundle, logger: Logger | undefined): AssetFrameworkGlobalState {
		Assert(this.getStateId() === bundle.originalStateId, 'DESYNC: Mismatch in state id when applying delta bundle');

		let space = this.space;
		if (bundle.space != null) {
			space = this.space.applyClientDeltaBundle(bundle.space, logger);
		}

		let characters = this.characters;
		if (bundle.characters != null) {
			const newCharacters = new Map(characters);
			for (const [key, characterData] of Object.entries(bundle.characters)) {
				const characterId = CharacterIdSchema.parse(key);
				if (characterData == null) {
					newCharacters.delete(characterId);
				} else if (characterData[0] === 'full') {
					newCharacters.set(
						characterId,
						AssetFrameworkCharacterState.loadFromBundle(this.assetManager, characterId, characterData[1], space, null, logger),
					);
				} else if (characterData[0] === 'delta') {
					const originalCharacter = this.characters.get(characterId);
					Assert(originalCharacter != null, 'DESYNC: Character to update not found');
					newCharacters.set(
						characterId,
						originalCharacter.applyClientDeltaBundle(characterData[1], space, logger),
					);
				} else {
					AssertNever(characterData);
				}
			}
			characters = newCharacters;
		}

		const resultState = new AssetFrameworkGlobalState(
			this.assetManager,
			characters,
			space,
			bundle.targetStateId,
		);

		Assert(resultState.isValid(), 'State is invalid after delta update');
		return resultState;
	}

	public produceCharacterState(character: CharacterId, producer: (currentState: AssetFrameworkCharacterState) => AssetFrameworkCharacterState | null): AssetFrameworkGlobalState | null {
		const currentState = this.getCharacterState(character);
		if (!currentState)
			return null;

		const newState = producer(currentState);
		if (!newState)
			return null;

		const newCharacters = new Map(this.characters);
		newCharacters.set(character, newState);

		return new AssetFrameworkGlobalState(
			this.assetManager,
			newCharacters,
			this.space,
		);
	}

	public produceSpaceState(producer: (currentState: AssetFrameworkSpaceState) => AssetFrameworkSpaceState | null): AssetFrameworkGlobalState | null {
		const newState = producer(this.space);
		if (!newState)
			return null;

		return this.withSpaceState(newState);
	}

	public withSpaceState(newState: AssetFrameworkSpaceState): AssetFrameworkGlobalState {
		if (this.space === newState)
			return this;

		const newCharacters = new Map(this.characters);
		for (const [id, character] of newCharacters) {
			const room = newState.getRoom(character.currentRoom);
			if (room != null) {
				newCharacters.set(
					id,
					character.updateRoomStateLink(room, false),
				);
			}
		}

		return new AssetFrameworkGlobalState(
			this.assetManager,
			newCharacters,
			newState,
		);
	}

	public withCharacter(characterId: CharacterId, characterState: AssetFrameworkCharacterState | null): AssetFrameworkGlobalState {
		if ((this.characters.get(characterId) ?? null) === characterState)
			return this;

		const newCharacters = new Map(this.characters);

		if (characterState == null) {
			newCharacters.delete(characterId);
		} else {
			Assert(characterId === characterState.id);
			newCharacters.set(characterId, characterState);
		}

		return new AssetFrameworkGlobalState(
			this.assetManager,
			newCharacters,
			this.space,
		);
	}

	public clearInvalidRoomDeviceSlotOccupancy(onlyCharacterId?: CharacterId): AssetFrameworkGlobalState {
		let newSpace = this.space;

		for (const room of this.space.rooms) {
			let changed = false;
			const newItems = room.items.map((item) => {
				if (!item.isType('roomDevice'))
					return item;

				for (const [slot, occupant] of item.slotOccupancy) {
					if ((onlyCharacterId == null || occupant === onlyCharacterId) && !this._isValidRoomDeviceSlotOccupancy(item, slot, occupant)) {
						item = item.changeSlotOccupancy(slot, null) ?? item;
						changed = true;
					}
				}

				return item;
			});

			if (changed) {
				newSpace = newSpace.produceRoom(room.id, (currentRoom) => currentRoom.produceWithItems(newItems)) ?? newSpace;
			}
		}

		return newSpace === this.space ? this : this.withSpaceState(newSpace);
	}

	private _isValidRoomDeviceSlotOccupancy(roomDevice: Item<'roomDevice'>, slot: string, characterId: CharacterId): boolean {
		const slotDefinition = roomDevice.asset.definition.slots[slot];
		if (slotDefinition == null)
			return false;

		const character = this.characters.get(characterId);
		if (character == null)
			return false;

		return character.items.some((item) =>
			item.isType('roomDeviceWearablePart') &&
			item.asset.id === slotDefinition.wearableAsset &&
			item.roomDeviceLink?.device === roomDevice.id &&
			item.roomDeviceLink.slot === slot,
		);
	}

	public runAutomaticActions(): AssetFrameworkGlobalState {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		let result: AssetFrameworkGlobalState = this;
		result = GlobalStateAutoProcessCharacterPositions(result);
		return result;
	}

	public listChanges(oldState: AssetFrameworkGlobalState): {
		space: boolean;
		characters: Set<CharacterId>;
	} {
		const characters = new Set<CharacterId>();

		// Check changed and added characters
		for (const [characterId, character] of this.characters) {
			if (oldState.characters.get(characterId) !== character) {
				characters.add(characterId);
			}
		}

		// Check removed characters
		for (const characterId of oldState.characters.keys()) {
			if (!this.characters.has(characterId)) {
				characters.add(characterId);
			}
		}

		return {
			space: this.space !== oldState.space,
			characters,
		};
	}

	public static createDefault(assetManager: AssetManager, spaceState: AssetFrameworkSpaceState): AssetFrameworkGlobalState {
		Assert(spaceState.assetManager === assetManager);
		const instance = new AssetFrameworkGlobalState(
			assetManager,
			new Map(),
			spaceState,
		);

		return freeze(instance, true);
	}

	public static loadFromBundle(assetManager: AssetManager, bundle: AssetFrameworkGlobalStateBundle, spaceId: SpaceId | null, logger: Logger | undefined): AssetFrameworkGlobalState {
		const characters = new Map<CharacterId, AssetFrameworkCharacterState>();

		const space = AssetFrameworkSpaceState.loadFromBundle(assetManager, bundle.space, spaceId, logger);

		for (const [key, characterData] of Object.entries(bundle.characters)) {
			AssertNotNullable(characterData);
			const characterId = CharacterIdSchema.parse(key);
			characters.set(
				characterId,
				AssetFrameworkCharacterState.loadFromBundle(
					assetManager,
					characterId,
					characterData,
					space,
					(room, forCharacter) => {
						return Array.from(characters.values())
							.filter((c) => c.id !== forCharacter && c.currentRoom === room.id)
							.map((c) => c.position.position);
					},
					logger,
				),
			);
		}

		const resultState = new AssetFrameworkGlobalState(
			assetManager,
			characters,
			space,
			bundle.stateId,
		);

		Assert(resultState.isValid(), 'State is invalid after load');

		return resultState;
	}
}

export type AssetFrameworkGlobalStateContainerEventHandler = (newState: AssetFrameworkGlobalState, oldState: AssetFrameworkGlobalState) => void;

/**
 * Mutable class that contains current global state and ensures the state is always valid.
 */
export class AssetFrameworkGlobalStateContainer {
	private readonly _logger: Logger;

	private _currentState: AssetFrameworkGlobalState;
	public get currentState(): AssetFrameworkGlobalState {
		return this._currentState;
	}

	private readonly _onChangeHandler: AssetFrameworkGlobalStateContainerEventHandler;

	constructor(logger: Logger, onChangeHandler: AssetFrameworkGlobalStateContainerEventHandler, initialState: AssetFrameworkGlobalState) {
		this._logger = logger;
		this._onChangeHandler = onChangeHandler;

		this._currentState = initialState;
		Assert(this._currentState.isValid());
	}

	public reloadAssetManager(assetManager: AssetManager) {
		if (this.currentState.assetManager === assetManager)
			return;
		const oldState = this._currentState;
		const bundle = oldState.exportToBundle();

		// Reset bundle state id, as change of assets definitions can be unpredictable
		bundle.stateId = nanoid(8);

		const newState = AssetFrameworkGlobalState.loadFromBundle(
			assetManager,
			bundle,
			oldState.space.spaceId,
			this._logger.prefixMessages('Asset manager reload:'),
		).runAutomaticActions();
		Assert(newState.isValid());
		this._currentState = newState;
		this._onChange(newState, oldState);
	}

	public setState(newState: AssetFrameworkGlobalState): void {
		Assert(newState.isValid(), 'Attempt to set invalid state');
		if (this._currentState === newState)
			return;

		const oldState = this._currentState;
		this._currentState = newState;
		this._onChange(newState, oldState);
	}

	private _onChange(newState: AssetFrameworkGlobalState, oldState: AssetFrameworkGlobalState) {
		this._onChangeHandler(newState, oldState);
	}
}
